import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Alert, Button } from 'antd';
import { useMaiaT } from '$app/i18n/useMaiaT';
import { getStoredSelectedSegment, SEGMENT_SELECTION_CHANGED_EVENT } from '$app/segmentSelection';
import { Header, Title } from '$app/shared/components/Header';
import { Container } from '$app/shared/components/Container';
import { downloadCsv, exportSegmentGarages, getImportTemplate, uploadImportFile } from './actions';
import { importSegment, MODES } from './importHelpers';

const MAX_FILE_BYTES = 5 * 1024 * 1024;

export function GarageImportUploadPage({ match, history }) {
  const t = useMaiaT();
  const mode = MODES[match.params.mode];
  const [segment, setSegment] = useState(() => importSegment(getStoredSelectedSegment()));
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleSegmentChange = () => setSegment(importSegment(getStoredSelectedSegment()));
    window.addEventListener(SEGMENT_SELECTION_CHANGED_EVENT, handleSegmentChange);
    return () => window.removeEventListener(SEGMENT_SELECTION_CHANGED_EVENT, handleSegmentChange);
  }, []);

  if (!segment || !mode) {
    return (
      <Container>
        <Alert
          type="info"
          showIcon
          message={t(
            'maia.garageImport.unavailable',
            'Garage imports are available for Motrio and Renault segments.'
          )}
        />
      </Container>
    );
  }

  const isCreate = mode === MODES.create;

  const chooseFile = (event) => {
    const chosen = event.target.files && event.target.files[0];
    setError(null);
    if (chosen && chosen.size > MAX_FILE_BYTES) {
      setFile(null);
      setError(t('maia.garageImport.fileTooLarge', 'The file is larger than 5 MB.'));
      return;
    }
    setFile(chosen || null);
  };

  const upload = async () => {
    setUploading(true);
    setError(null);
    try {
      const job = await uploadImportFile({ segment, mode, file });
      history.push(`/imports/${job.jobId}`);
    } catch (err) {
      console.error('GarageImportUploadPage upload error', { segment, mode, err });
      const serverMessage = err.response && err.response.data && err.response.data.message;
      setError(
        serverMessage ||
          t('maia.garageImport.uploadError', 'The file could not be uploaded. Please try again.')
      );
      setUploading(false);
    }
  };

  const download = (load) =>
    load()
      .then(downloadCsv)
      .catch((err) => {
        console.error('GarageImportUploadPage download error', { segment, mode, err });
        setError(t('maia.garageImport.downloadError', 'The file could not be downloaded. Please try again.'));
      });

  return (
    <Container>
      <Header>
        <Title>
          {isCreate
            ? t('maia.garageImport.mode.create', 'Create garages')
            : t('maia.garageImport.mode.update', 'Update garages')}
        </Title>
        <div data-testid="import-segment">{segment}</div>
      </Header>

      <div style={{ padding: 16, maxWidth: 720 }}>
        {!isCreate && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('maia.garageImport.uploadInfoTitle', 'Before you upload')}
            description={
              <>
                <p style={{ margin: '0 0 4px', fontWeight: 600 }}>
                  {t('maia.garageImport.locationInfoTitle', 'Latitude, longitude and address')}
                </p>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li>
                    {t(
                      'maia.garageImport.locationInfoPin',
                      'Latitude and longitude move the pin on the map.'
                    )}
                  </li>
                  <li>
                    {t(
                      'maia.garageImport.locationInfoAddress',
                      'The address only changes the text shown on the website. It does not move the pin.'
                    )}
                  </li>
                  <li>
                    {t(
                      'maia.garageImport.locationInfoMaps',
                      'To find the coordinates in Google Maps, search for the garage, right-click the exact spot and click the coordinates at the top of the menu (for example 33.5731, -7.5898). The first number is the latitude and the second is the longitude.'
                    )}
                  </li>
                </ul>
                <p style={{ margin: '12px 0 4px', fontWeight: 600 }}>
                  {t('maia.garageImport.marketingInfoTitle', 'Marketing preferences')}
                </p>
                <p style={{ margin: '0 0 4px' }}>
                  {t(
                    'maia.garageImport.marketingInfoIntro',
                    'Email and SMS are separate. Use one of these values:'
                  )}
                </p>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li>
                    <code>ACCEPT</code>
                    {' — '}
                    {t('maia.garageImport.marketingAccept', 'the garage has opted in.')}
                  </li>
                  <li>
                    <code>DECLINE</code>
                    {' — '}
                    {t('maia.garageImport.marketingDecline', 'the garage has opted out.')}
                  </li>
                  <li>
                    <code>PRESELECT</code>
                    {' — '}
                    {t(
                      'maia.garageImport.marketingPreselect',
                      'treated as opted in, but the garage has not confirmed it yet. When they confirm, a ticked box is saved as ACCEPT and an unticked box as DECLINE.'
                    )}
                  </li>
                </ul>
                <p style={{ margin: '12px 0 4px', fontWeight: 600 }}>
                  {t('maia.garageImport.statusInfoTitle', 'Status')}
                </p>
                <p style={{ margin: '0 0 4px' }}>
                  {t('maia.garageImport.statusInfoIntro', 'Use one of these values:')}
                </p>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  <li>
                    <code>ACTIVE</code>
                    {' — '}
                    {t('maia.garageImport.statusActive', 'online bookings.')}
                  </li>
                  <li>
                    <code>SUSPENDED</code>
                    {' — '}
                    {t('maia.garageImport.statusSuspended', 'telephone bookings.')}
                  </li>
                  <li>
                    <code>DELETED</code>
                  </li>
                </ul>
              </>
            }
          />
        )}
        <p>
          {isCreate
            ? t(
                'maia.garageImport.createHelp',
                'Upload a CSV with one new garage per row. New garages are created as Suspended. Every row is checked before anything is imported.'
              )
            : t(
                'maia.garageImport.updateHelp',
                'Download the current garages, change the cells you need and upload the file. Blank cells are left unchanged. Every row is checked before anything is imported.'
              )}
        </p>

        <div style={{ marginBottom: 16 }}>
          <Button onClick={() => download(() => getImportTemplate(segment, mode))} style={{ marginRight: 8 }}>
            {t('maia.garageImport.downloadTemplate', 'Download empty template')}
          </Button>
          {!isCreate && (
            <Button onClick={() => download(() => exportSegmentGarages(segment))}>
              {t('maia.garageImport.downloadGarages', 'Download current garages')}
            </Button>
          )}
        </div>

        <label htmlFor="garage-import-file" style={{ display: 'block', marginBottom: 8 }}>
          {t('maia.garageImport.chooseFile', 'CSV file (UTF-8, comma or semicolon, up to 5 MB)')}
        </label>
        <input id="garage-import-file" type="file" accept=".csv,text/csv" onChange={chooseFile} />

        {error && <Alert type="error" showIcon style={{ marginTop: 12 }} message={error} />}

        <div style={{ marginTop: 16 }}>
          <Button type="primary" disabled={!file} loading={uploading} onClick={upload}>
            {t('maia.garageImport.uploadAndCheck', 'Upload and check')}
          </Button>
          <Button type="link" onClick={() => history.push('/imports')}>
            {t('maia.garageImport.cancel', 'Cancel')}
          </Button>
        </div>
      </div>
    </Container>
  );
}

export default withRouter(GarageImportUploadPage);
